import {
  AfterContentInit,
  Attribute,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  inject,
  Injector,
  NgZone, OnDestroy,
  Optional,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { CommonModule, DecimalPipe } from "@angular/common";
import { FormBuilder, FormGroup, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { debounce, extend, findIndex, floor, get, indexOf, isArray, isObject, isUndefined, keys, noop, omitBy, some, split, startsWith } from 'lodash-es';

import { DISPLAY_TYPE, IWidgetConfig, StylableComponent, provideAsWidgetRef, Parent, register, styler, TrustAsPipe, WmComponentsModule, NAVIGATION_TYPE, EDIT_MODE, getRowDef } from "@wm/components-base";
import { cloneDeep, find, forEach, includes, isEmpty, isEqual, remove, set, toNumber } from 'lodash-es';
import {
  $appDigest,
  Viewport,
  getDatasourceFromExpr,
  isDefined,
  isMobile,
  triggerFn,
  getValidJSON,
  DataSource,
  runtimeEventManager,
  $unwatch,
  $watch,
  App,
  $invokeWatchers,
  extendProto,
  isDataSourceEqual,
  getClonedObject, getVariableName, getBoundWidgetDetails, toBoolean,
  getWatchIdentifier,
  ScriptLoaderService,
  fetchPropertiesMapColumns,
  getFieldsForService,
  resetObjectWithEmptyValues
} from '@wm/core';
import { getRowOperationsColumn, prepareFieldDefs, transformData, extractDataSourceName } from '@wm/components-base';
import { PaginationComponent } from '../../pagination/pagination.component';
import { ListComponent } from '../list/list.component';
import { getConditionalClasses, provideAs } from '@wm/components-base';
import { Observable, Subject, debounceTime } from 'rxjs';
import { LivetableComponent } from "../livetable/livetable.component";
import { MenuComponent } from '../../navigation/menu/menu.component';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TableRowActionDirective } from './table-row-action/table-row-action.directive';
import { TableFilterDirective } from './table-filter/table-filter.directive';
import { BasicPagination } from '../basic-pagination/basic-pagination.component';

const DEFAULT_CLS = 'app-grid app-panel panel';
const WIDGET_CONFIG: IWidgetConfig = {
  widgetType: 'wm-table',
  hostClass: DEFAULT_CLS,
  displayType: DISPLAY_TYPE.INLINE_BLOCK
};

const ROW_OPS_FIELD = 'rowOperations';

const exportIconMapping: any = {
  EXCEL: 'fa fa-file-excel-o',
  CSV: 'fa fa-file-text-o'
};

// get the variable object using name - datasource
// hardcode the dataset value

@Component({
  selector: '[wmTable]',
  template: `<div class="panel-heading" *ngIf="title || subheading || iconclass || exportOptions.length || _actions.header.length">
  <h3 class="panel-title">
      <div class="pull-left">
          <i class="app-icon panel-icon {{iconclass}}" *ngIf="iconclass"></i>
      </div>
      <div class="pull-left">
          <div class="heading" [innerHTML]="title"></div>
          <div class="description" [innerHTML]="subheading"></div>
      </div>
      <div class="panel-actions app-datagrid-actions" *ngIf="exportOptions.length || _actions.header.length">
          <ng-container *ngFor="let btn of _actions.header"
                        [ngTemplateOutlet]="btn.widgetType === 'button' ? buttonRef : anchorRef" [ngTemplateOutletContext]="{btn:btn}">
          </ng-container>
          <div name="export" wmMenu dropdown caption="Export" *ngIf="exportOptions.length"  autoclose="always"
              dataset.bind="exportOptions" select.event="export($item)" menuposition="down,left"></div>
      </div>
  </h3>
</div>
<h6 *ngIf="bundleLoading">{{appLocale.MESSAGE_LOADING}}</h6>
<div class="app-datagrid" #datagridElement></div>
  <div class="on-demand-datagrid"><a [hidden]="!(shownavigation && navControls === 'On-Demand')" href="javascript:void(0);" class="app-button btn btn-block on-demand-load-btn">{{ondemandmessage}}</a></div>
  <div class="panel-footer clearfix" *ngIf="shownavigation">
  <!--Dummy Pagination-->
  <div class="app-datagrid-paginator">
    <basicPagination
        [navigationAlign]="navigationalign"
        [navigationSize]="navigationSize"
        [navigationType]="navigation"
        [dataset]="dataset"
        [pageCount]="pageCount"
        [showrecordcount]="showrecordcount"
        [currentPage]="currentPage">
    </basicPagination>
  </div>
  <div class="app-datagrid-actions" *ngIf="_actions.footer.length">
      <ng-container *ngFor="let btn of _actions.footer"
                    [ngTemplateOutlet]="btn.widgetType === 'button' ? buttonRef : anchorRef" [ngTemplateOutletContext]="{btn:btn}">
      </ng-container>
  </div>
</div>

<ng-template #buttonRef let-btn="btn">
    <button name="{{btn.key}}" class="btn app-button {{btn.class}}" [hidden]="!btn.show"
    [ngClass]="{'btn-sm': spacing === 'condensed', 'disabled-new': btn.key === 'addNewRow' && (isGridEditMode || isLoading)}" tabindex="{{btn.tabindex}}"
    title="{{btn.title}}" [disabled]="btn.disabled">
      <i class="{{btn.iconclass}} app-icon"></i>
      <span class="ps-1 btn-caption">{{btn.displayName}}</span>
    </button>
  </ng-template>

  <ng-template #anchorRef let-btn="btn">
    <a name="{{btn.key}}" class="app-anchor {{btn.class}}" [hidden]="!btn.show"
    [ngClass]="{'btn-sm': spacing === 'condensed', 'disabled-new': btn.key === 'addNewRow' && (isGridEditMode || isLoading)}" tabindex="{{btn.tabindex}}"
    title="{{btn.title}}">
      <i class="app-icon {{btn.iconclass}}"></i>
      <span class="anchor-caption">{{btn.displayName}}</span>
    </a>
  </ng-template>

<div hidden class="hidden-elements">
  <ng-container #multiColumnFilterView></ng-container>

  <ng-container #inlineEditView></ng-container>

  <ng-container #inlineEditNewView></ng-container>

  <ng-container #rowActionsView></ng-container>

  <ng-container #rowExpansionActionView></ng-container>

  <ng-container #customExprView></ng-container>

  <ng-container #dynamicTable></ng-container>

  <ng-container #rowDetailView></ng-container>

  <div class="dynamic-table-container"></div>
</div>`,
  providers: [
    provideAs(TableComponent, NG_VALUE_ACCESSOR, true),
    provideAsWidgetRef(TableComponent),
    FormBuilder,
    DecimalPipe
  ],
  exportAs: 'wmTable',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent, WmComponentsModule, MenuComponent, BsDropdownModule, TableRowActionDirective, BasicPagination],
  styles: []
})
export class TableComponent extends StylableComponent implements AfterContentInit, OnDestroy {
  static initializeProps = register('wm-table');
  @ViewChild(PaginationComponent) dataNavigator: PaginationComponent;
  @ViewChild('datagridElement', { static: true }) private _tableElement: ElementRef;
  @ContentChildren('rowActionTmpl', { descendants: true }) rowActionTmpl: QueryList<any>;
  @ViewChild('rowActionsView', { static: true, read: ViewContainerRef }) rowActionsViewRef: ViewContainerRef;

  @ContentChildren('filterTmpl', { descendants: true }) filterTmpl: QueryList<any>;
  @ViewChild('multiColumnFilterView', { static: true, read: ViewContainerRef }) filterViewRef: ViewContainerRef;

  @ContentChildren('inlineWidgetTmpl', { descendants: true }) inlineWidgetTmpl: QueryList<any>;
  @ViewChild('inlineEditView', { static: true, read: ViewContainerRef }) inlineEditViewRef: ViewContainerRef;

  @ContentChildren('inlineWidgetTmplNew', { descendants: true }) inlineWidgetNewTmpl: QueryList<any>;
  @ViewChild('inlineEditNewView', { static: true, read: ViewContainerRef }) inlineEditNewViewRef: ViewContainerRef;

  @ContentChildren('customExprTmpl', { descendants: true }) customExprTmpl: QueryList<any>;
  @ViewChild('customExprView', { static: true, read: ViewContainerRef }) customExprViewRef: ViewContainerRef;

  @ContentChildren('rowExpansionActionTmpl') rowExpansionActionTmpl: QueryList<any>;
  @ContentChild('rowExpansionTmpl') rowExpansionTmpl: TemplateRef<any>;
  @ViewChild('rowDetailView', { static: true, read: ViewContainerRef }) rowDetailViewRef: ViewContainerRef;
  @ViewChild('rowExpansionActionView', { static: true, read: ViewContainerRef }) rowExpansionActionViewRef: ViewContainerRef;

  @ViewChild('dynamicTable', { static: true, read: ViewContainerRef }) dynamicTableRef: ViewContainerRef;
  showClearIcon = noop;
  bundleLoading = false;

  sizeClasses: any = {
    'Pager': {
      'small': 'pager-sm',
      'large': 'pager-lg'
    },
    'Basic': {
      'small': 'pagination-sm',
      'large': 'pagination-lg'
    },
    'Classic': {
      'small': 'pagination-sm',
      'large': 'pagination-lg'
    }
  };
  private inlineCompliedTl: any = {};
  private inlineNewCompliedTl: any = {};
  private trustAsPipe: TrustAsPipe;
  customExprCompiledTl: any = {};
  customExprCompiledSummaryTl: any = {};
  rowFilterCompliedTl: any = {};
  rowDefInstances: any = {};
  rowDefMap: any = {};
  rowActionsCompiledTl: any = {};
  rowExpansionActionTl: any = {};
  shownewrow: any;
  formfields: any = {};
  title: string;
  subheading: string;
  iconclass: string;
  exportOptions: any[] = [];
  items: any[] = [];
  _actions: { header: any[], footer: any[] } = {
    'header': [],
    'footer': []
  };
  rowngclass: any;
  shownavigation = false;
  navigationalign: any;
  showrecordcount: any;
  name: string = '';
  pagesize: number;
  spacing: string;
  isGridEditMode: boolean = false;
  isLoading: boolean;
  onDemandLoad: boolean;
  _liveTableParent: any;
  actions: any[] = [];
  datagridElement: any;
  gridElement: any;
  navControls: any;
  _pageLoad = true;
  public statehandler: any;
  nodatamessage: any;
  loadingdatamsg: any;
  sortInfo: any;
  ngform: FormGroup;
  emptyMatchModes: any;
  getNavigationTargetBySortInfo: Function;
  filternullrecords: any;
  getFilterFields: Function;
  checkFiltersApplied: Function;
  getSearchResult: Function;
  getSortResult: Function;
  addNewRow: Function;
  addRow: Function;
  insertRecord: Function;
  deleteRow: Function;
  editRow: Function;
  initiateSelectItem: Function;
  updateVariable: Function;
  updateRecord: Function;
  deleteRecord: Function;
  onRecordDelete: Function;
  hideEditRow: Function;
  saveRow: Function;
  cancelRow: Function;
  onRowFilterChange = noop;
  _isClientSearch: any;
  _triggeredByUser: any;
  _isDependent: any;
  serviceData: any;
  filterInfo: any;
  serverData: any;
  _dynamicContext: any;
  rowDef: any;
  rowInstance: any;
  multiselect: any;
  radioselect: any;
  isNewRowInserted: any;
  isDataLoading: any;
  editmode: any;
  errormessage: any; //TODO: should be move to form component
  updatemessage: any;
  insertmessage: any;
  onRowupdate: any;
  onRowinsert: any;
  deleteoktext;
  deletecanceltext;
  selectedColumns: any;

  matchModeTypesMap: any;
  fullFieldDefs: any = [];
  rowFilter: any = {};
  rowActions: any[] = [];
  headerConfig: any[] = [];
  rowclass: any;
  formposition: any;
  filtermode: any;
  searchlabel: any;
  navigation: any;
  _gridData: any[];
  fieldDefs: any = [];
  primaryKey: any = [];
  columns: any = {};
  noOfColumns: any;
  isdynamictable: boolean;
  isRowDeleted: any;
  infScroll: any;
  actionRowIndex: any;
  actionRowPage: any;
  _dataset: any;
  isPartOfLiveGrid: any;
  datasource: any;
  navigationSize: any;
  private applyProps = new Map();
  prevFilterExpression: any;
  _selectedItemsExist = false;
  gridfirstrowselect: any;
  isrowselectable:any;
  allowpagesizechange: boolean;
  pagesizeoptions: string;
  dataNavigatorWatched: any;
  __fullData: any;
  variableInflight: any;
  private dynamicComponentProvider: any;
  currentPage: any;
  onError: any;
  navigatorResultWatch: any;
  navigatorMaxResultWatch: any;
  variableService: any;
  isGridPartOfLiveGrid: boolean;
  prevData: any;
  updatedMarkup: any;
  ondemandmessage: any;
  redraw = debounce(this._redraw, 150);
  private scriptLoaderService = inject(ScriptLoaderService);
  multiselecttitle:string;
  multiselectarialabel: string;
  radioselecttitle:string;
  radioselectarialabel:string

  selectedItemChange = new Subject();
  selectedItemChange$: Observable<any> = this.selectedItemChange.asObservable();
  parentNode: any;
  public gridOptions: any = {
    data: [],
    colDefs: [],
    startRowIndex: 1,
    sortInfo: {
      field: '',
      direction: ''
    },
    filtermode: '',
    searchLabel: '',
    rowActions: [],
    headerConfig: [],
    rowClass: '',
    editmode: '',
    formPosition: '',
    isMobile: false,
    rowExpansionEnabled: false,
    rowDef: {
      position: '0'
    },
    name: '',
    messages: {
      selectField: 'Select Field'
    },
    securityUtils: {
      pipeTransform: {}
    },
    navigation: '',
    deletedRowIndex: -1,
    lastActionPerformed: 'scroll',
    isSearchTrigerred: false,
    isDatasetUpdated: false,
    ACTIONS: {
      'DELETE': 'delete',
      'EDIT': 'edit',
      'SEARCH_OR_SORT': 'search_or_sort',
      'DEFAULT': 'scroll',
      'FILTER_CRITERIA': 'filter',
      'DATASET_UPDATE': 'dataset_update'
    },
    actionRowIndex: undefined,
    actionRowPage: undefined,
    isLastPage: false,
    // get the current page number
    getCurrentPage: () => {
      return get(this.dataNavigator, 'dn.currentPage') || 1;
    },
    // set the page number
    setCurrentPage: (pageNum: any) => {
      set(this.dataNavigator, 'dn.currentPage', pageNum || 1);
    },
    // get the page limit
    getPageSize: () => {
      return this.pagesize || 5;
    },
    // set the deleted row index
    setDeletedRowIndex: (id: any) => {
      this.setDataGridOption('deletedRowIndex', id);
    },
    // sets the last action performed like edit, delete etc.
    setLastActionPerformed: (action: any) => {
      this.setDataGridOption('lastActionPerformed', action || this.gridOptions.lastActionPerformed);
    },
    isNavTypeScrollOrOndemand: () => {
      return this.gridOptions.navigation === 'Scroll' || this.gridOptions.navigation === 'On-Demand';
    },
    setIsSearchTrigerred: (value: any) => {
      this.setDataGridOption('isSearchTrigerred', value);
    },
    setIsDatasetUpdated: (value: any) => {
      this.setDataGridOption('isDatasetUpdated', value);
    },
    onRowInsert: (row: any, e: any, callBack: any, options: any) => {
      this.insertRecord(extend({}, options, { row, event: e, 'callBack': callBack }));
    },
    beforeRowUpdate: (row: any, eventName?: any) => {
      if (this._liveTableParent) {
        this._liveTableParent.updateRow(row, eventName);
      }
      this.prevData = getClonedObject(row);
    },
    onBeforeRowUpdate: (row: any, e: any, options: any) => {
      return this.invokeEventCallback('beforerowupdate', { $event: e, $data: row, row, options: options });
    },
    onBeforeRowInsert: (row: any, e: any, options: any) => {
      return this.invokeEventCallback('beforerowinsert', { $event: e, $data: row, row, options: options });
    },
    registerRowNgClassWatcher: (rowData: any, index: any) => {
      if (!this.rowngclass) {
        return;
      }
      const row = this.getClonedRowObject(rowData);
      const watchName = `${this.widgetId}_rowNgClass_${index}`;
      $unwatch(watchName);
      //[Todo-CSP]: generate watcher expr in page if rowngclass attr is present for table
      this.registerDestroyListener($watch(this.rowngclass, this.viewParent, { row }, (nv: any, ov: any) => {
        this.callDataGridMethod('applyRowNgClass', getConditionalClasses(nv, ov), index);
      }, watchName));
      runtimeEventManager.on(this.widgetId + 'table-compiled').subscribe(() => {
        if (this.gridElement.find('tbody').length) {
          let watchInfo = getWatchIdentifier(watchName);
          const ngClassObj = watchInfo.fn.bind(watchInfo.expr, this, { row })();
          this.callDataGridMethod('applyRowNgClass', getConditionalClasses(ngClassObj), index);
        }
      });
    },
    registerColNgClassWatcher: (rowData: any, colDef: any, rowIndex: any, colIndex: any) => {
      if (!colDef['col-ng-class']) {
        return;
      }
      const row = this.getClonedRowObject(rowData);
      const watchName = `${this.widgetId}_colNgClass_${rowIndex}_${colIndex}`;
      $unwatch(watchName);
      //[Todo-CSP]: generate watcher expr in page if col-ng-class attr is present for table
      this.registerDestroyListener($watch(colDef['col-ng-class'], this.viewParent, { row }, (nv: any, ov: any) => {
        this.callDataGridMethod('applyColNgClass', getConditionalClasses(nv, ov), rowIndex, colIndex);
      }, watchName));
      runtimeEventManager.on(this.widgetId + 'table-compiled').subscribe(() => {
        if (this.gridElement.find('tbody').length) {
          let watchInfo = getWatchIdentifier(watchName);
          const ngClassObj = watchInfo.fn.bind(watchInfo.expr, this, { row })();
          this.callDataGridMethod('applyColNgClass', getConditionalClasses(ngClassObj), rowIndex, colIndex);
        }
      });
    },
    getCustomExpression: (fieldName: string, index: any, summaryRow?: any) => {
      let customExpression;
      if (!summaryRow) {
        customExpression = this.customExprCompiledTl[fieldName + index];
      } else {
        customExpression = this.customExprCompiledSummaryTl[fieldName + index];
      }
      return customExpression || '';
    },
    clearCustomExpression: () => {
      this.customExprViewRef.clear();
      this.customExprCompiledTl = {};
      this.customExprCompiledSummaryTl = {};
    },
    clearRowDetailExpression: () => {
      this.rowDetailViewRef.clear();
      this.rowDefMap = {};
      this.rowDefInstances = {};
    },
    clearRowActions: () => {
      this.rowActionsViewRef.clear();
      this.rowActionsCompiledTl = {};
      this.rowExpansionActionViewRef.clear();
      this.rowExpansionActionTl = {};
    },
    clearActionRowIndex: () => {
      if (!this.isRowDeleted && (this.infScroll || this.onDemandLoad)) {
        this.clearActionRowVars();
      }
    },
    setFieldValue: (fieldName: any, value: any) => {
      const control = this.ngform.controls && this.ngform.controls[fieldName];
      if (control) {
        control.setValue(value);
      }
    },
    getFieldValue: (fieldName: any) => {
      const control = this.ngform.controls && this.ngform.controls[fieldName];
      return control && control.value;
    },
    setGridEditMode: (val: any) => {
      this.ngZone.run(() => {
        this.isGridEditMode = val;
        $appDigest();
      });
    },
    setGridState: (val: any) => {
      this.isLoading = val === 'loading';
    },
    generateCustomExpressions: (rowData: any, index: any, summaryRow?: any) => {
      const row = this.getClonedRowObject(rowData);
      const compileTemplate = (tmpl: any) => {
        if (!tmpl) {
          return;
        }
        const colDef = {};
        const context = {
          row,
          colDef
        };
        this.addEventsToContext(context);
        const customExprView = this.customExprViewRef.createEmbeddedView(tmpl, context);
        const rootNode = customExprView.rootNodes[0];
        const fieldName = rootNode.getAttribute('data-col-identifier');
        this.applyRowContextToCustomWidget(rootNode, context);
        extend(colDef, this.columns[fieldName]);
        if (!summaryRow) {
          this.customExprCompiledTl[fieldName + index] = rootNode;
        } else {
          this.customExprCompiledSummaryTl[fieldName + index] = rootNode;
        }
      };
      if (this.isdynamictable) {
        this.fieldDefs.forEach((col: any) => {
          compileTemplate(col.customExprTmpl);
        });
        return;
      }
      // For all the columns inside the table, generate the custom expression
      this.customExprTmpl.forEach(compileTemplate.bind(this));
    },
    generateRowActions: (rowData: any, index: any) => {
      const row = this.getClonedRowObject(rowData);
      this.rowActionsCompiledTl[index] = [];
      // For all the columns inside the table, generate the inline widget
      this.rowActionTmpl.forEach((tmpl: any) => {
        this.rowActionsCompiledTl[index].push(...this.rowActionsViewRef?.createEmbeddedView(tmpl, { row }).rootNodes);
      });
    },
    getRowAction: (index: any) => {
      return this.rowActionsCompiledTl[index];
    },
    generateRowExpansionCell: (rowData: any, index: any) => {
      const row = this.getClonedRowObject(rowData);
      // For all the columns inside the table, generate the inline widget
      this.rowExpansionActionTmpl.forEach((tmpl) => {
        this.rowExpansionActionTl[index] = this.rowExpansionActionViewRef.createEmbeddedView(tmpl, { row }).rootNodes;
      });
    },
    getRowExpansionAction: (index: any) => {
      return this.rowExpansionActionTl[index];
    },
    onBeforeRowCollapse: ($event: any, row: any, rowId: string) => {
      return this.rowInstance.invokeEventCallback('beforerowcollapse', { $event, row, $data: this.rowDefInstances[rowId] });
    },
    onRowCollapse: ($event: any, row: any) => {
      this.rowInstance.invokeEventCallback('rowcollapse', { $event, row });
    },
    generateRowDetailView: ($event: any, rowData: any, rowId: any, $target: any, $overlay: any, callback: any) => {
      const row = this.getClonedRowObject(rowData);
      const rowDef = cloneDeep(this.rowDef);
      if (this.rowInstance.invokeEventCallback('beforerowexpand', { $event, $data: rowDef, row }) === false) {
        return;
      }
      if (!rowDef.content) {
        return;
      }
      // Expand the row detail
      callback();
      // Row is already rendered. Return here
      if ((this.rowDefMap as any)[rowId] && (this.rowDefMap as any)[rowId].content === rowDef.content) {
        this.rowInstance.invokeEventCallback('rowexpand', { $event, row, $data: (this.rowDefInstances as any)[rowId] });
        return;
      }
      (this.rowDefMap as any)[rowId] = rowDef;
      $target.empty();
      $target.hide();
      $overlay.show();
      const context = {
        row,
        rowDef,
        containerLoad: (widget: any) => {
          setTimeout(() => {
            $overlay.hide();
            $target.show();
            (this.rowDefInstances as any)[rowId] = widget;
            this.rowInstance.invokeEventCallback('rowexpand', { $event, row, $data: widget });
          }, 500);
        }
      };
      const rootNode = this.rowDetailViewRef.createEmbeddedView(this.rowExpansionTmpl, context).rootNodes[0];
      $target[0].appendChild(rootNode);
      $appDigest();
    },
    // Function to redraw the widgets on resize of columns
    redrawWidgets: () => {
      this.fieldDefs.forEach((col: any) => {
        triggerFn(col.inlineInstance && col.inlineInstance.redraw);
        triggerFn(col.inlineInstanceNew && col.inlineInstanceNew.redraw);
        triggerFn(col.filterInstance && col.filterInstance.redraw);
      });
    },
    runInNgZone: (fn: any) => {
      this.ngZone.run(fn);
    },
    safeApply: () => {
      $appDigest();
    },
    // assigns the items on capture phase of the click handler.
    assignSelectedItems: (row: any, e: any) => {
      this.ngZone && this.ngZone.run(() => {
        /*
         * in case of single select, update the items with out changing the reference.
         * for multi select, keep old selected items in tact
         */
        if (this.multiselect) {
          if (findIndex(this.items, row) === -1) {
            this.items.push(row);
          }
        } else {
          this.items.length = 0;
          this.items.push(row);
        }
      });
    },
    callOnRowClickEvent: (row: any, e: any) => {
      // WMS-18774: If we invoke navigation action variable on rowclick callback, the navigation is triggering outside angular zone so adding ngZone.run
      this.ngZone && this.ngZone.run(() => {
        // Call row click only if click is triggered by user
        if (e && e.hasOwnProperty('originalEvent')) {
          const rowData = this.addRowIndex(row);
          this.invokeEventCallback('rowclick', { $data: rowData, $event: e, row: rowData });
        }
      });
    },
    onColumnSelect: (col: any, e: any) => {
      this.selectedColumns = this.callDataGridMethod('getSelectedColumns');
      this.invokeEventCallback('columnselect', { $data: col, $event: e });
    },
    onColumnDeselect: (col: any, e: any) => {
      this.selectedColumns = this.callDataGridMethod('getSelectedColumns');
      this.invokeEventCallback('columndeselect', { $data: col, $event: e });
    },
    onHeaderClick: (col: any, e: any) => {
      // if onSort function is registered invoke it when the column header is clicked
      this.invokeEventCallback('headerclick', { $event: e, $data: col, column: col });
    },
    generateFilterRow: () => {
      // Clear the view container ref
      this.filterViewRef.clear();
      this.rowFilterCompliedTl = {};
      // For all the columns inside the table, generate the compiled filter template
      this.filterTmpl.forEach((tmpl: any) => {
        const rootNode = this.filterViewRef.createEmbeddedView(tmpl, {
          changeFn: this.onRowFilterChange.bind(this),
          isDisabled: (fieldName: any) => {
            return this.emptyMatchModes.indexOf(this.rowFilter[fieldName] && this.rowFilter[fieldName].matchMode) > -1;
          }
        }).rootNodes[0];
        this.rowFilterCompliedTl[rootNode.getAttribute('data-col-identifier')] = rootNode;
      })
    },
    getFilterWidget: (fieldName: any) => {
      // Move the generated filter template to the filter row
      return this.rowFilterCompliedTl[fieldName];
    },
    // Function to apply validators to Inline form controls
    applyValidations: (col: any, alwaysNewRow: any) => {
      if (alwaysNewRow) {
        triggerFn(col.applyNewRowValidations && col.applyNewRowValidations.bind(col));
      } else {
        triggerFn(col.applyValidations && col.applyValidations.bind(col));
      }
    },
    // Function to remove validators and set form state to untouched for inline form control
    removeValidations: (alwaysNewRow: any) => {
      this.fullFieldDefs.forEach((col: any) => {
        if (!col.readonly && col.binding !== 'rowOperations') {
          if (alwaysNewRow) {
            triggerFn(col.removeNewRowValidations && col.removeNewRowValidations.bind(col));
          } else {
            triggerFn(col.removeValidations && col.removeValidations.bind(col));
          }
        }
      });
    },
    generateInlineEditRow: (rowData: any, alwaysNewRow: any) => {
      const row = this.getClonedRowObject(rowData);
      if (alwaysNewRow) {
        // Clear the view container ref
        this.inlineEditNewViewRef.clear();
        this.inlineNewCompliedTl = {};
        // For all the columns inside the table, generate the inline widget
        this.inlineWidgetNewTmpl.forEach(tmpl => {
          let fieldName: any;
          const context = {
            row,
            getControl: () => {
              return this.ngform.controls[fieldName + '_new'] || {};
            },
            getValidationMessage: () => {
              return this.columns[fieldName] && this.columns[fieldName].validationmessage;
            },
            getPendingSpinnerStatusNew: () => {
              return this.columns[fieldName] && this.columns[fieldName].showPendingSpinnerNew;
            }
          };
          const rootNode = this.inlineEditNewViewRef.createEmbeddedView(tmpl, context).rootNodes[0];
          fieldName = rootNode.getAttribute('data-col-identifier');
          this.inlineNewCompliedTl[fieldName] = rootNode;
        });
        this.clearForm(true);
        return;
      }
      // Clear the view container ref
      this.inlineEditViewRef.clear();
      this.inlineCompliedTl = {};
      this.clearForm();
      // For all the columns inside the table, generate the inline widget
      this.inlineWidgetTmpl.forEach(tmpl => {
        let fieldName: any;
        const context = {
          row,
          getControl: () => {
            return this.ngform.controls[fieldName] || {};
          },
          getValidationMessage: () => {
            return this.columns[fieldName] && this.columns[fieldName].validationmessage;
          },
          getPendingSpinnerStatus: () => {
            return this.columns[fieldName] && this.columns[fieldName].showPendingSpinner;
          }
        };
        const rootNode = this.inlineEditViewRef.createEmbeddedView(tmpl, context).rootNodes[0];
        fieldName = rootNode.getAttribute('data-col-identifier');
        this.inlineCompliedTl[fieldName] = rootNode;
      });
    },
    getInlineEditWidget: (fieldName: any, value: any, alwaysNewRow: any) => {
      if (alwaysNewRow) {
        this.gridOptions.setFieldValue(fieldName + '_new', value);
        return this.inlineNewCompliedTl[fieldName];
      }
      this.gridOptions.setFieldValue(fieldName, value);
      return this.inlineCompliedTl[fieldName];
    },
    timeoutCall: (fn: any, delay: any) => {
      setTimeout(fn, delay);
    },
    onDataRender: () => {
      // after grid renders, check and apply class for gridFirstRowSelect property
      this.toggleFirstRowSelectClass();
    }
  };
  private gridVariable: any;
  private markupManager: any;
  pageCount: any;
  _searchSortHandler = noop;
  searchSortHandler = (...args: any[]) => { this._searchSortHandler.apply(this, args); };
  private isBoundToStaticVariable: boolean;
  private isBoundToLiveVariable: boolean;
  private isBoundToLiveVariableRoot: boolean;
  private isBoundToServiceVariable: boolean;
  private isBoundToDataServiceType: boolean;
  private isBoundToProcedureServiceVariable: boolean;
  private isBoundToQueryServiceVariable: boolean;
  private isBoundToSelectedItem: boolean;
  private isBoundToSelectedItemSubset: boolean;
  private isBoundToFilter: boolean;
  private isBoundToVariable: boolean;
  private isBoundToWidget: boolean;
  private variableName: any;
  private variable: any;
  private variableType: any;
  private isBoundToWebSocketVariable: boolean;
  private widgetName: any;
  private appManager: any;

  get dataset() {
    return this._dataset
  }
  set dataset(val: any) {
    this._dataset = val;
    this.setFlags();
    let innerDataSetRegEx = /dataSet(\[\$i\])?\./;
    if ((this.isBoundToVariable && innerDataSetRegEx.test(val)) || this.isBoundToSelectedItem || this.isBoundToSelectedItemSubset) {
      //If bound to inner dataset, defualt value is not present. So, dataset watch may not be triggered. To prevent loading icon showing continuously, show no data found.
      this.callDataGridMethod('setStatus', 'nodata', this.nodatamessage);
    }
    if (includes(val, 'selecteditem.')) {
      this.callDataGridMethod('setStatus', 'error', this.appLocale.MESSAGE_GRID_CANNOT_LOAD_DATA_IN_STUDIO);
    }
    this.setWidgetPropsBasedOnFlags();
    // Set grid columns on binddataset change if grid is bound to service variable
    if ((this.isBoundToServiceVariable || this.isBoundToWebSocketVariable)) {
      //If columns are to be created, call the create grid columns method
      if (this.isdynamictable) {
        let typeUtils = runtimeEventManager.getService('TypeFactoryService');
        this.createGridColumns(typeUtils.getFieldsForExpr(this.binddataset, {
          'getFieldInfo': true,
          'filter': 'terminal'
        }));
      }
      this.callDataGridMethod('setStatus', 'error', this.appLocale.MESSAGE_GRID_CANNOT_LOAD_DATA_IN_STUDIO);
    }
  }

  setWidgetPropsBasedOnFlags() {
    /* Disable/Update the properties in properties panel which are dependent on binddataset value. */
    /*Make the "pageSize" property hidden so that no editing is possible for live and query service variables*/
    this.widgetProps['pagesize'].show = !(this.isBoundToLiveVariable || this.isBoundToQueryServiceVariable) || (this.isBoundToWidget ? !this.isBoundToFilter : false);
    this.widgetProps['exportformat'].show = this.widgetProps['exportformat'].showindesigner = this.widgetProps['exportdatasize'].show = this.widgetProps['exportdatasize'].showindesigner = (this.showExportOptions());

    /* If bound to live filter result, disable grid search. */
    this.widgetProps['filtermode'].disabled = this.isBoundToFilter;
  }

  constructor(inj: Injector,
    @Optional() viewParent: Parent,
    public fb: FormBuilder,
    private viewport: Viewport,
    @Optional() public parentList: ListComponent,
    @Attribute('dataset.bind') public binddataset: any,
    @Attribute('datasource.bind') public binddatasource: any,
    @Attribute('readonlygrid') public readonlygrid: any,
    @Optional() public parentLiveTable: LivetableComponent,
    private ngZone: NgZone) {
    super(inj, WIDGET_CONFIG, viewParent);
    styler(this.nativeElement, this);
    this.isdynamictable = toBoolean(this.$attrs.get('isdynamictable'));
    runtimeEventManager.off('dataset-modified' + '_' + this.widgetId);
    runtimeEventManager.on('dataset-loading' + '_' + this.widgetId).subscribe(() => {
      this.callDataGridMethod('setStatus', 'loading', this.loadingdatamsg);
    })
    // dataset change listener for liveVariable
    runtimeEventManager.on('dataset-modified' + '_' + this.widgetId).subscribe((newDataset: any) => {
      runtimeEventManager.off('dataset-loading' + '_' + this.widgetId);
      this.callDataGridMethod('setStatus', 'ready');
      this._dataset = newDataset;
      this.populateGridData(newDataset);
      this.setLastActionToDatasetUpdate();
      this.callDataGridMethod('refreshGridData');
    })
    this.markupManager = this.getMarkupManager();
    this.dynamicComponentProvider = runtimeEventManager.getService('dynamicComponentRefProvider');
    this.variableService = runtimeEventManager.getService('variableService');
    this.appManager = runtimeEventManager.getService('appManager');
    let eventName = 'updateTableMarkup' + this.widgetId;
    runtimeEventManager.off(eventName);
    runtimeEventManager.on(eventName).subscribe((async (markup: any) => {
      this.$element.find('.dynamic-table-container')[0].innerHTML = '';
      runtimeEventManager.off(this.widgetId + 'table-compiled');
      let tableRef = this.$element.attr('table_reference');
      const componentFactoryRef = await this.dynamicComponentProvider.getComponentFactoryRef(
        'app-table-dynamic-' + this.widgetId,
        markup[0].outerHTML,
        {
          noCache: true,
          transpile: true,
          viewContainerRef: this.dynamicTableRef
        });
      extendProto(componentFactoryRef.instance, this.widget.context);
      this.parentNode = this.$element[0].parentNode;
      const $childEl = $(this.parentNode).find('[widget-id="' + this.widgetId + '"]');
      const dynamicEl = this.$element.find('.hidden-elements').find('[widget-id="' + this.widgetId + '"]');
      const cloneEl = dynamicEl;
      cloneEl.attr('table_reference', tableRef);
      this.parentNode.insertBefore(cloneEl[0], $childEl[0]);
      cloneEl[0].widgetparentLiveTable = this.parentLiveTable;
      setTimeout(() => {
        cloneEl.trigger('click');
        runtimeEventManager.emit('reset-active-canvas-el');
        this.getMarkupManager()?.setActiveCanvasEl(cloneEl);
        runtimeEventManager.emit(this.widgetId + 'table-compiled');
        $childEl[0].remove();
        $(this.parentNode).find('.wm-focus-rect').remove();
        runtimeEventManager.emit('set-active-widget', this.widgetId);
      });
    }).bind(this));
    this.ngform = fb.group({});
    this.addEventsToContext(this.context);

    this.deleteoktext = this.appLocale.LABEL_OK;
    this.deletecanceltext = this.appLocale.LABEL_CANCEL;
  }

  getMarkupManager() {
    if (!this.markupManager) {
      this.markupManager = runtimeEventManager.getService('LiveWidgetMarkupManagerService')?.markupManagerService;
    }
    return this.markupManager;
  }

  override ngOnInit() {
    if(this.getAttr('subheading.bind'))
      this.subheading='bind:'+this.getAttr('subheading.bind');
    super.ngOnInit();
    if (!this.parentLiveTable) {
      this.parentLiveTable = (this.$element.closest('.app-livegrid')[0] as any)?.widget;
    }
    this.parentLiveTable?.initLiveTable(this);
    this._liveTableParent = this.parentLiveTable;
    this.parentNode = this.$element[0].parentNode;

    if (this._liveTableParent) {
      this.isPartOfLiveGrid = true;
      this.widgetProps['multiselect'].show = this.widgetProps['multiselect'].showindesigner = (this.isPartOfLiveGrid ? false : this.widgetProps['multiselect'].show);
    }
  }

  set selecteditem(val: any) {
    // Select the rows in the table based on the new selected items passed
    this.items.length = 0;
    this.callDataGridMethod('selectRows', val);
  }

  set gridData(newValue) {
    this.isDataLoading = false;
    this.variableInflight = false;
    if (this.gridOptions?.isNavTypeScrollOrOndemand()) {
      // update the _gridData field with the next set of items and modify the current page
      // [this._gridData, this.currentPage] = this.paginationService.updateFieldsOnPagination(this, newValue);

      // When edit action is performed on previous page, update the _gridData field with the modified row data
      if (this.gridOptions.lastActionPerformed === this.gridOptions.ACTIONS.EDIT) {
        const rowIndex = floor(this.actionRowIndex % this.pagesize);
        this._gridData.splice(this.actionRowIndex, 1, newValue[rowIndex]);
      }
      this.setDataGridOption('isLastPage', !!(this.dataNavigator?.isDisableNext));
      // In case of on demand pagination, create the load more button only once and show the button until next page is not disabled
      if (!this.$element.find('.on-demand-datagrid').length && this.onDemandLoad) {
        this.callDataGridMethod('addLoadMoreBtn', this.ondemandmessage, this.loadingdatamsg, ($event: any) => {
          this.dataNavigator.navigatePage('next', $event);
          this.isDataLoading = true;
        });
      }
    }
    this._gridData = newValue;

    let startRowIndex = 0;
    let gridOptions;

    if (isDefined(newValue)) {
      if (this._gridData.length && this.infScroll) {
        // smoothscroll events will be binded.
        // Added timeout as the table html is rendered at runtime
        setTimeout(() => {
          // this.paginationService.bindScrollEvt(this, 'tbody', DEBOUNCE_TIMES.PAGINATION_DEBOUNCE_TIME);
        }, 0);
      }

      /*Setting the serial no's only when show navigation is enabled and data navigator is compiled
       and its current page is set properly*/
      if (this.isNavigationEnabled() && this.dataNavigator.dn.currentPage) {
        startRowIndex = ((this.dataNavigator.dn.currentPage - 1) * (this.dataNavigator.maxResults || 1)) + 1;
        this.setDataGridOption('startRowIndex', startRowIndex);
      }
      /* If colDefs are available, but not already set on the datagrid, then set them.
       * This will happen while switching from markup to design tab. */
      gridOptions = this.callDataGridMethod('getOptions');

      if (!gridOptions) {
        return;
      }

      if (!gridOptions.colDefs.length && this.fieldDefs.length) {
        this.setDataGridOption('colDefs', getClonedObject(this.fieldDefs));
      }
      // If data and colDefs are present, call on before data render event
      if (!this.isdynamictable && !isEmpty(newValue) && gridOptions.colDefs.length) {
        this.invokeEventCallback('beforedatarender', { $data: this._gridData, $columns: this.columns, data: this._gridData, columns: this.columns });
      }
      this.setDataGridOption('data', getClonedObject(this._gridData));
    }
  }

  get gridData() {
    return this._gridData || [];
  }

  setDataGridElement() {
    this.datagridElement = $(this._tableElement?.nativeElement);
  }

  async ngAfterContentInit(): Promise<void> {
    this.setFlags();
    this.setWidgetPropsBasedOnFlags();
    this.headerConfig = this.headerConfig.filter(this.filterEmptyValues.bind(this));
    remove(this.fieldDefs, f => f === undefined);
    const runModeInitialProperties = {
      showrowindex: 'showRowIndex',
      multiselect: 'multiselect',
      radioselect: 'showRadioColumn',
      filternullrecords: 'filterNullRecords',
      enablesort: 'enableSort',
      showheader: 'showHeader',
      enablecolumnselection: 'enableColumnSelection',
      shownewrow: 'showNewRow',
      isrowselectable:'isrowselectable',
      allowpagesizechange: 'allowpagesizechange'
    };
    if (this._liveTableParent) {
      this.isPartOfLiveGrid = true;
    }
    this.editmode = this.$attrs.get('editmode');
    if (this.readonlygrid || !this.editmode) {
      if (this.readonlygrid === 'true') {
        this.editmode = '';
      } else {
        if (this.isPartOfLiveGrid) {
          this.editmode = this.isPartOfLiveGrid.formlayout === 'inline' ? EDIT_MODE.FORM : EDIT_MODE.DIALOG;
        } else {
          this.editmode = this.readonlygrid ? EDIT_MODE.INLINE : '';
        }
      }
    }

    this.gridOptions.colDefs = this.fullFieldDefs;
    this.gridOptions.rowActions = this.rowActions;
    this.gridOptions.headerConfig = this.headerConfig;
    this.gridOptions.rowClass = this.rowclass;
    this.gridOptions.editmode = this.editmode;
    this.gridOptions.formPosition = this.formposition;
    this.gridOptions.filtermode = this.filtermode;
    this.gridOptions.searchLabel = this.nativeElement.getAttribute('searchlabel.bind') ? '' : this.searchlabel;
    this.gridOptions.isMobile = false;
    this.gridOptions.name = this.name;
    this.gridOptions.securityUtils.pipeTransform = this.trustAsPipe;
    this.gridOptions.navigation = this.navigation;

    // When loadondemand property is enabled(deferload="true") and show is true, only the column titles of the datatable are rendered, the data(body of the datatable) is not at all rendered.
    // Because the griddata is setting before the datatable dom is rendered but we are sending empty data to the datatable.
    if (!isEmpty(this.gridData)) {
      this.gridOptions.data = cloneDeep(this.gridData);
    }
    this.gridOptions.messages = {
      'selectField': this.appLocale.MESSAGE_SELECT_FIELD
    };

    this.datagridElement = $(this._tableElement.nativeElement);
    this.gridElement = this.$element;
    this.$element.css({ 'position': 'relative' });

    forEach(runModeInitialProperties, (value, key) => {
      if (isDefined((this as any)[key])) {
        this.gridOptions[value] = ((this as any)[key] === 'true' || (this as any)[key] === true);
      }
    });
    this.renderOperationColumns();
    this.gridOptions.colDefs = this.fieldDefs;

    if(!this.datagridElement.hasOwnProperty('datatable')) {
      this.bundleLoading = true;
      const { loaded, error } =  await this.scriptLoaderService.loadLazyScript('datatable-bundle');
      this.bundleLoading = false;
      if(!loaded) {
        console.error("Failed to load datatable-bundle", error);
        return;
      }
    }

    this.datagridElement.datatable(this.gridOptions);
    !(this.isBoundToServiceVariable || this.isBoundToWebSocketVariable) && this.callDataGridMethod('setStatus', 'loading', this.loadingdatamsg);

    this.applyProps.forEach(args => this.callDataGridMethod(...args));
  }

  // removes empty values in headerConfig array.
  filterEmptyValues(item: any) {
    if (item.columns) {
      item.columns = item.columns.filter(this.filterEmptyValues.bind(this));
    }
    return item !== undefined;
  }

  registerActions(tableAction: any) {
    this.actions.push(tableAction);
    this.populateActions();
  }

  populateActions() {
    this._actions.header = [];
    this._actions.footer = [];
    forEach(this.actions, (action) => {

      if (includes(action.position, 'header')) {
        this._actions.header.push(action);
      }
      if (includes(action.position, 'footer')) {
        this._actions.footer.push(action);
      }
    });
  }

  private handleStateParams(widgetState: any, options: any) {
    if (get(widgetState, 'selectedItem')) {
      this._selectedItemsExist = true;
    }
    options.options = options.options || {};
    if (get(widgetState, 'pagination')) {
      options.options.page = widgetState.pagination;
      if (get(widgetState, 'sort')) {
        this.sortStateHandler(widgetState);
        options.options.orderBy = get(widgetState, 'sort.field') + ' ' + get(widgetState, 'sort.direction');
      }
      if (get(widgetState, 'search')) {
        setTimeout(() => {
          this.searchStateHandler(widgetState);
        }, 500);
        options.options.filterFields = this.getFilterFields(widgetState.search);
      }
    } else {
      options.options.page = 1;
      if (get(widgetState, 'search')) {
        setTimeout(() => {
          this.searchStateHandler(widgetState);
        }, 500);
        options.options.filterFields = this.getFilterFields(widgetState.search);
      }
      if (get(widgetState, 'sort')) {
        this.sortStateHandler(widgetState);
        options.options.orderBy = get(widgetState, 'sort.field') + ' ' + get(widgetState, 'sort.direction');
      }
    }
    return options;
  }

  private sortStateHandler(widgetState: any) {
    const $gridElement = this.datagridElement;
    const $sortIcon = $gridElement.find('th[data-col-field="' + get(widgetState, 'sort.field') + '"] .sort-icon');
    if (get(widgetState, 'sort.direction') === 'asc' && $sortIcon.length) {
      $sortIcon.addClass('asc wi wi-long-arrow-up');
    } else if (get(widgetState, 'sort.direction') === 'desc' && $sortIcon.length) {
      $sortIcon.addClass('desc wi wi-long-arrow-down');
    }
  }

  private searchStateHandler(widgetState: any) {
    if (isArray(widgetState.search)) {
      forEach(widgetState.search, (filterObj) => {
        if (this.rowFilter[filterObj.field]) {
          this.rowFilter[filterObj.field].value = filterObj.value;
          this.rowFilter[filterObj.field].matchMode = filterObj.matchMode;
          this.rowFilter[filterObj.field].type = filterObj.type;
          if ($(this.rowFilterCompliedTl[filterObj.field]).length) {
            const val = filterObj.type === 'integer' ? parseInt(filterObj.value) : filterObj.value;
            $(this.rowFilterCompliedTl[filterObj.field]).find('input').val(filterObj.value);
          }
        }
      });
    } else {
      const $gridElement = this.datagridElement;
      $gridElement.find('[data-element="dgSearchText"]').val(widgetState.search.value);
      $gridElement.find('[data-element="dgFilterValue"]').val(widgetState.search.field);
    }
  }

  setDataGridOption(optionName: any, newVal: any, forceSet = false) {
    if (!this.datagridElement || !this.datagridElement.datatable || !this.datagridElement.datatable('instance')) {
      return;
    }
    if (optionName === 'selectFirstRow') {
      return this.toggleFirstRowSelectClass();
    }
    const option: any = {};
    if ((!isEqual(newVal, this.gridOptions[optionName]) || forceSet)) {
      option[optionName] = newVal;
      this.datagridElement.datatable('option', option);
      this.gridOptions[optionName] = newVal;
    }
  }

  // this method will render the filter row.
  renderDynamicFilterColumn(filteTemRef: TemplateRef<any>) {
    // For dynamic table manually pushing the filtertemplateRef as templateRef will not be available prior.
    if (this.isdynamictable) {
      (this.filterTmpl as any)?._results.push(filteTemRef);
    }
  }

  registerColumns(tableColumn: any, colIndex: any) {
    // TODO: Commenting: as not required as all columns need to be shown in studio.
    // if (isMobile() && this.viewport.isMobileType) {
    //   if (!tableColumn.mobileDisplay) {
    //     return;
    //   }
    // } else if (this.viewport.isTabletType) {
    //   if (!tableColumn.tabletDisplay) {
    //     return;
    //   }
    // } else {
    //   if (!tableColumn.pcDisplay) {
    //     return;
    //   }
    // }
    if (tableColumn['primary-key']) {
      this.primaryKey.push(tableColumn.field);
    }
    this.fieldDefs[colIndex] = tableColumn;
    const colCount = this.fieldDefs.length;
    this.fullFieldDefs.push(tableColumn);
    this.rowFilter[tableColumn.field] = {
      value: undefined
    };
    this.fieldDefs.forEach((col: any) => {
      this.columns[col.field] = col;
    });
    // If dynamic datatable and last column, pass the columns to jquery datatable
    if (this.isdynamictable && colCount === this.noOfColumns) {
      this.renderOperationColumns();
      this.setDataGridOption('colDefs', this.fieldDefs);
    }
  }

  registerRow(tableRow: any, rowInstance: any) {
    this.rowDef = tableRow;
    this.rowInstance = rowInstance;
    this.callDataGridMethod('option', 'cssClassNames.rowExpandIcon', this.rowDef.expandicon);
    this.callDataGridMethod('option', 'cssClassNames.rowCollapseIcon', this.rowDef.collapseicon);
    this.gridOptions.rowExpansionEnabled = true;
    this.gridOptions.rowDef = this.rowDef;
    this.rowDef = getRowDef(tableRow);
    this.rowDef.partialParams = [];
    let paramEleCollection   = this.markupManager?.activeMarkupEl[0]?.querySelector('[widget-id='+this.widgetId+']')?.getElementsByTagName('wm-table-row')[0]?.getElementsByTagName('wm-param');
    if(!paramEleCollection) {
      paramEleCollection = this.markupManager?.activeMarkupEl[0]?.getElementsByTagName('wm-table-row')[0]?.getElementsByTagName('wm-param');
    }
    forEach(paramEleCollection, (param) => {
      this.rowDef.partialParams.push({ 'name': param.getAttribute('name'), 'value': param.getAttribute('value'), 'type': param.getAttribute('type') })
    })

  }

  renderOperationColumns() {
    let rowActionCol,
      insertPosition;

    // TODO: check and add live utils
    const rowOperationsColumn: any = getRowOperationsColumn(),
      config: any = {
        'name': rowOperationsColumn.field,
        'field': rowOperationsColumn.field,
        'isPredefined': true
      };
    // Return if no fieldDefs are present
    if (!this.fieldDefs.length) {
      return;
    }

    rowActionCol = find(this.fullFieldDefs, { 'field': ROW_OPS_FIELD, type: 'custom' }); // Check if column is fetched from markup
    remove(this.fieldDefs, { type: 'custom', field: ROW_OPS_FIELD }); // Removing operations column
    remove(this.headerConfig, { field: rowOperationsColumn.field });

    /*Add the column for row operations only if at-least one operation has been enabled.*/
    if (this.rowActions.length) {
      if (rowActionCol) { // If column is present in markup, push the column or push the default column
        insertPosition = rowActionCol.rowactionsposition ? toNumber(rowActionCol.rowactionsposition) : this.fieldDefs.length;
        this.fieldDefs.splice(insertPosition, 0, rowActionCol);
        if (insertPosition === 0) {
          this.headerConfig.unshift(config);
        } else {
          this.headerConfig.push(config);
        }
      } else {
        this.fieldDefs.push(rowOperationsColumn);
        this.headerConfig.push(config);
      }
    }
    this.setDataGridOption('headerConfig', this.headerConfig);
  }

  override onPropertyChange(key: string, nv: any, ov?: any) {
    let enableNewRow;
    switch (key) {
      case 'dataset':
        this._dataset = nv;
        this.watchVariableDataSet(nv);
        break;
      case 'filtermode':
        this.setDataGridOption('filtermode', nv);
        if (this.widgetId) {
          this.widgetProps['onBeforefilter'].show = !!nv;
        }
        break;
      case 'searchlabel':
        this.setDataGridOption('searchLabel', this.$attrs.get('searchlabel.bind') ? '' : nv);
        break;
      case 'navigation':
        if (nv === 'Advanced') { // Support for older projects where navigation type was advanced instead of classic
          this.navigation = 'Classic';
          return;
        }
        if (nv !== 'None') {
          this.shownavigation = true;
        }
        if (this.widgetId) {
          this.widgetProps['showrecordcount'].show = this.widgetProps['showrecordcount'].showindesigner = !includes(['None', 'Pager'], nv);
          this.widgetProps['onSetrecord'].show = nv !== 'None';
          if (includes([NAVIGATION_TYPE.SCROLL, NAVIGATION_TYPE.ONDEMAND], nv)) {
            this.statehandler = 'none';
          } else if (this.statehandler === 'none' && includes([NAVIGATION_TYPE.SCROLL, NAVIGATION_TYPE.ONDEMAND], ov)) {
            this.statehandler = 'URL';
          }
          this.markupManager.setAttr(this.widgetId, { 'statehandler': this.statehandler });
        }
        this.setDataGridOption('navigation', this.navigation);
        this.onDemandLoad = nv === NAVIGATION_TYPE.ONDEMAND ? true : false;
        this.infScroll = nv === NAVIGATION_TYPE.SCROLL ? true : false;
        this.navControls = nv;
        break;
      case 'gridfirstrowselect':
        this.setDataGridOption('selectFirstRow', nv);
        break;
      case 'gridclass':
        this.callDataGridMethod('option', 'cssClassNames.grid', nv);
        break;
      case 'nodatamessage':
        !(this.isBoundToServiceVariable || this.isBoundToWebSocketVariable) && this.callDataGridMethod('option', 'dataStates.nodata', nv);
        break;
      case 'loadingdatamsg':
        !(this.isBoundToServiceVariable || this.isBoundToWebSocketVariable) && this.callDataGridMethod('option', 'dataStates.loading', nv);
        break;
      case 'loadingicon':
        this.callDataGridMethod('option', 'loadingicon', nv);
        break;
      case 'spacing':
        this.callDataGridMethod('option', 'spacing', nv);
        if (nv === 'condensed') {
          this.navigationSize = 'small';
        } else {
          this.navigationSize = '';
        }
        break;
      case 'exportformat':
        this.exportOptions = [];
        if (nv) {
          // Populate options for export drop down menu
          forEach(split(nv, ','), type => {
            this.exportOptions.push({
              label: type,
              icon: exportIconMapping[type]
            });
          });
        }
        if (this.widgetId) {
          this.widgetProps['onBeforeexport'].show = (this.exportOptions.length > 0);
        }
        break;
      case 'shownewrow':
        // Enable new row if shownew is true or addNewRow buton is present
        enableNewRow = nv || some(this.actions, act => includes(act.action, 'addNewRow()'));
        this.callDataGridMethod('option', 'actionsEnabled.new', enableNewRow);
        break;
      case 'ondemandmessage':
        if (nv) {
          this.$element.find('.on-demand-datagrid > a').text(nv);
        }
        break;
      case 'show':
        if (nv) {
          this.invokeEventCallback('show');
        } else {
          this.invokeEventCallback('hide');
        }
        super.onPropertyChange(key, nv, ov);
        break;
      case 'height':
        this.callDataGridMethod('setGridDimensions', 'height', nv ? nv : 'auto');
        break;
      case 'showheader':
        this.setDataGridOption('showHeader', nv);
        if (this.widgetId) {
          this.widgetProps['onHeaderclick'].show = nv === true;
        }
        break;
      case 'enablesort':
        if (this.widgetId) {
          this.widgetProps['onSort'].show = nv === true;
        }
        break;
      case 'editmode':
        if (this.widgetId) {
          const isFormMode = (this.editmode === EDIT_MODE.DIALOG || this.editmode === EDIT_MODE.FORM);
          let wp = this.widgetProps;
          wp['onBeforerowinsert'].show = !isFormMode;
          wp['onRowinsert'].show = !isFormMode;
          wp['onBeforerowupdate'].show = !isFormMode;
          wp['onRowupdate'].show = !isFormMode;
          wp['onFormrender'].show = !isFormMode;
          wp['onBeforeformrender'].show = !isFormMode;
        }
        break;
      case 'multiselect':
        if (this.widgetId) {
          if (nv) {
            this.radioselect = false;
            this.widgetProps['radioselect'].disabled = false;
            this.markupManager.setAttr(this.widgetId, { 'radioselect': false });
          }
          this.setDataGridOption('multiselect', nv);
        }
        break;
        case 'multiselecttitle':
            if (this.widgetId) {
                this.setDataGridOption('multiselecttitle', nv);
            }
            break;
        case 'multiselectarialabel':
            if (this.widgetId) {
                this.setDataGridOption('multiselectarialabel', nv);
            }
            break;
        case 'isrowselectable':
            this.setDataGridOption('isrowselectable', nv);
          break;
      case 'allowpagesizechange':
        this.setDataGridOption('allowpagesizechange', nv);
        break;
      case 'pagesizeoptions':
        this.setDataGridOption('pagesizeoptions', nv);
        break;
      case 'radioselect':
        if (this.widgetId) {
          if (nv) {
            this.multiselect = false;
            this.widgetProps['multiselect'].disabled = false;
            this.markupManager.setAttr(this.widgetId, { 'multiselect': false });
          }
          this.setDataGridOption('showRadioColumn', nv);
        }
        break;
        case 'radioselecttitle':
            if (this.widgetId) {
                this.setDataGridOption('radioselecttitle', nv);
            }
            break;
        case 'radioselectarialabel':
            if (this.widgetId) {
                this.setDataGridOption('radioselectarialabel', nv);
            }
            break;
      default:
        super.onPropertyChange(key, nv, ov);
    }
  }

  callDataGridMethod(...args: any[]) {
    this.setDataGridElement();
    if (!this.datagridElement || !this.datagridElement.datatable?.('instance')) {
      this.applyProps?.set(args[1], args);
      return; // If datagrid is not initiliazed or destroyed, return here
    }
    return this.datagridElement.datatable.apply(this.datagridElement, args);
  }

  onDataSourceChange() {
    this.fieldDefs.forEach((col: any) => {
      triggerFn(col.onDataSourceChange && col.onDataSourceChange.bind(col));
    });
  }

  setLastActionToDatasetUpdate() {
    this.gridOptions.setLastActionPerformed(this.gridOptions.ACTIONS.DATASET_UPDATE);
    this.gridOptions.setIsDatasetUpdated(true);
  }

  // Update the lastActionPerformed to Filter_Criteria, when there is change in the Variable filter criteria rules
  checkIfVarFiltersApplied() {
    if (!isEmpty(get(this.datasource, 'filterExpressions.rules')) || !isEmpty(get(this.datasource, 'dataBinding'))) {
      const currentFilterExpr = get(this.datasource, 'filterExpressions.rules') ? this.datasource.filterExpressions.rules : [].concat(this.datasource.dataBinding);
      const isEqual = this.compareFilterExpressions(this.prevFilterExpression, currentFilterExpr);
      if (!isEqual) {
        this.setLastActionToFilterCriteria();
      }
    }
  }

  onDataNavigatorDataSetChange(nv: any) {
    let data;
    this.__fullData = nv;
    // this.checkFiltersApplied(this.getSortExpr());
    if (this._isClientSearch) {
      data = cloneDeep(this.__fullData);
      if (isObject(data) && !isArray(data)) {
        data = [data];
      }
      data = this.getSearchResult(data, this.filterInfo);
      data = this.getSortResult(data, this.sortInfo);
      return data;
    }
    return nv;
  }

  // Compares the prevFilterCriteria Rules with the new rules
  compareFilterExpressions(prevFilters: any, newFilters: any) {
    return !!((prevFilters.length === newFilters.length) && (isEqual(prevFilters, newFilters)));
  }

  // Set the table lastActionPerformed to Filter Criteria and maintain the prevFilterExpression
  setLastActionToFilterCriteria() {
    this.prevFilterExpression = get(this.datasource, 'filterExpressions.rules') ? cloneDeep(this.datasource.filterExpressions.rules) : cloneDeep([].concat(this.datasource.dataBinding));
    this.gridOptions.setLastActionPerformed(this.gridOptions.ACTIONS.FILTER_CRITERIA);
    this.gridOptions.setIsSearchTrigerred(true);
  }

  watchVariableDataSet(newVal: any) {
    let result, isupdate;
     if(typeof newVal === 'string' && newVal.startsWith('bind:')){
      if ((this.isBoundToServiceVariable || this.isBoundToWebSocketVariable)) {
      // If columns are to be created, call the create grid columns method
        let typeUtils = runtimeEventManager.getService('TypeFactoryService');
        this.updatedGridColumns(typeUtils.getFieldsForExpr(this.binddataset, {
          'getFieldInfo': true,
          'filter': 'terminal'
        }));
        return;
    }
      let variableObj= this.variableService.getVariableInstance(newVal, "");
      newVal = variableObj.dataSet;
      isupdate = true
    }
    // Check for Variable filters if applied
    if (this.gridOptions?.isNavTypeScrollOrOndemand()) {
      this.checkIfVarFiltersApplied();
    }
    // State handling for static variables
    if (get(this.datasource, 'category') === 'wm.Variable' && this._pageLoad) {
      this._pageLoad = false;
      this.setDataGridOption('selectFirstRow', this.gridfirstrowselect);
    }
    // After the setting the watch on navigator, dataset is triggered with undefined. In this case, return here.
    if (this.dataNavigatorWatched && isUndefined(newVal) && this.__fullData) {
      return;
    }
    // If variable is in loading state, show loading icon
    if (this.variableInflight && !this.infScroll) {
      this.callDataGridMethod('setStatus', 'loading', this.loadingdatamsg);
    }

    result = getValidJSON(newVal);

    // Converting newval to object if it is an Object that comes as a string "{"data" : 1}"
    if (result) {
      newVal = result;
    }

    const sortExp = this.getSortExpr();
    const sortExpArr = sortExp.split(' ');
    if (sortExp && sortExpArr.length) {
      this.sortInfo = {
        direction: sortExpArr[1],
        field: sortExpArr[0]

      };
    }

    /*Return if data is invalid.*/
    if (!this.isDataValid()) {
      return;
    }

    if (!isObject(newVal) || !(newVal) || (newVal && !isUndefined((newVal as any).dataValue) && (newVal as any).dataValue === '')) {
      //Added to remove the table heading in case of empty dataset
      if (newVal === "" || (newVal && newVal.dataValue === "")) {
        this.fieldDefs = [];
        this.headerConfig = []
        this.gridVariable = []
        this.setDataGridOption('colDefs', getClonedObject(this.fieldDefs));
      }
      if (!this.variableInflight) {
        // If variable has finished loading and resultSet is empty, ender empty data
        this.setGridData([]);
        if (this.isBoundToServiceVariable) {
          this.callDataGridMethod('setStatus', 'error', this.appLocale['MESSAGE_GRID_CANNOT_LOAD_DATA_IN_STUDIO']);
        }
      }
      return;
    }
    if (newVal) {
      if (this.widgetId && isupdate) {
        const data = this.isBoundToLiveVariableRoot ? (newVal as any)?.data ?? newVal : newVal;
        this.updatedGridColumns(data, (newVal as any).propertiesMap || undefined);
      }
      this.populateGridData(newVal);
      if (this.isBoundToServiceVariable) {
        // Checking if grid is bound to service variable, for which data cannot be loaded in studio mode
        this.gridData = [];
        this.callDataGridMethod('setStatus', 'error', this.appLocale['MESSAGE_GRID_CANNOT_LOAD_DATA_IN_STUDIO']);
      }
    }
  }

   updatedGridColumns(data:any, propertiesMap?:any) {
     let dataValid = !!data && !data.error;
     if (dataValid && !Array.isArray(data)) {
       data = [data];
     }
       if (propertiesMap) {
         this.prepareFieldDefs(data, propertiesMap);
       } else {
         this.prepareFieldDefs(data);
       }
     if (dataValid) {
       this.serverData = data;
       this.setGridData(this.serverData);
     }
    }

  prepareFieldDefs(data: any[], propertiesMap?: any): void {
    let defaultFieldDefs: any;
    let properties: any[];
    let columns: Record<string, any> = {};
    const options: any = {};

    this.fieldDefs = [];
    this.headerConfig = [];

    if (propertiesMap) {
      columns = fetchPropertiesMapColumns(propertiesMap);
      properties = [resetObjectWithEmptyValues(columns)];
    } else {
      properties = data;
    }

    if ((this.isBoundToServiceVariable || this.isBoundToWebSocketVariable)) {
      defaultFieldDefs = getFieldsForService(properties);
    } else {
      defaultFieldDefs = prepareFieldDefs(properties, options);
    }

    defaultFieldDefs.forEach((columnDef: any) => {
      // Default display flags
      columnDef.pcDisplay = true;
      columnDef.mobileDisplay = true;
      columnDef.tabletDisplay = true;
      columnDef.searchable = true;
      columnDef.type = 'string';

      // Apply customizations from fullFieldDefs
      this.fullFieldDefs?.forEach((column: any) => {
        if (column.field && column.field === columnDef.field) {
          Object.assign(columnDef, {
            pcDisplay: column.pcDisplay,
            mobileDisplay: column.mobileDisplay,
            tabletDisplay: column.tabletDisplay,
            customExpression: column.customExpression,
            width: column.width,
            textAlignment: column.textAlignment,
            backgroundColor: column.backgroundColor,
            textColor: column.textColor,
            widgetType: column.widgetType,
            displayName: column.displayName,
            class: column.class,
            ngclass: column.ngclass,
            formatpattern: column.formatpattern,
            custompipeformat: column.custompipeformat,
            datepattern: column.datepattern,
            currencypattern: column.currencypattern,
            fractionsize: column.fractionsize,
            suffix: column.suffix,
            prefix: column.prefix,
            accessroles: column.accessroles
          });
        }
      });

      // Handle fields from propertiesMap
      if (propertiesMap) {
        const newColumn = columns[columnDef.field];
        columnDef.type = newColumn.type;
        columnDef.primaryKey = newColumn.isPrimaryKey;
        columnDef.generator = newColumn.generator;

        columnDef.readonly = isDefined(newColumn.readonly) ? newColumn.readonly === 'true' : newColumn.relatedEntityName ? !newColumn.isPrimaryKey
            : ['identity', 'uniqueid', 'sequence'].includes(newColumn.generator);
      }

      // Push to header config
      this.headerConfig.push({
        name: columnDef.field,
        field: columnDef.field
      });
    });

    this.fieldDefs = getClonedObject(defaultFieldDefs);
    this.renderOperationColumns();

    const gridObj = {
      widgetName: this.name,
      widgetId: this.widgetId,
      fieldDefs: defaultFieldDefs,
    };

    runtimeEventManager.getService('LiveWidgetMarkupManagerService').updateMarkupForGrid(gridObj);
    this.setDataGridOption('colDefs', getClonedObject(this.fieldDefs));
  }


  // Function to populate the grid with data.
  populateGridData(serviceData: any) {
    let data;
    if (this.datasource?.propertiesMap) {
      this.gridVariable = { data: serviceData, propertiesMap: this.datasource?.propertiesMap }
    }
    else this.gridVariable = serviceData;
    serviceData = transformData(serviceData, this.name);
    // Apply filter and sort, if data is refreshed through Refresh data method
    if (!this.isNavigationEnabled() && this._isClientSearch) {
      data = cloneDeep(serviceData);
      data = this.getSearchResult(data, this.filterInfo);
      data = this.getSortResult(data, this.sortInfo);
      this.serverData = data;
    } else {
      this.serverData = serviceData;
    }
    // If fielddefs are not present, generate fielddefs from data
    // Removing fielddefs check because When loadondemand property is enabled(deferload="true"), the dataset propertychangehanlder is triggered first before the dom is getting rendered.
    // So at that time fielddefs length is zero, due to this the columns are created dynamically.
    if (this.isdynamictable) {
      this.createGridColumns(this.serverData);
    } else {
      this.setGridData(this.serverData);
    }
  }

  createGridColumns(data: any) {
    /* this call back function receives the data from the variable */
    /* check whether data is valid or not */
    const dataValid = data && !data.error;
    /*if the data is type json object, make it an array of the object*/
    if (dataValid && !isArray(data)) {
      data = [data];
    }
    /* if the data is empty, show nodatamessage */
    if (isEmpty(data)) {
      this.setGridData(data);
      return;
    }

    if (!dataValid) {
      return;
    }
    /* if new columns to be rendered, prepare default fieldDefs for the data provided*/
    this.prepareColDefs(data);

    this.serverData = data;
    this.setGridData(this.serverData);
  }

  prepareColDefs(data: any) {
    let defaultFieldDefs: any;
    let properties;
    let columnIndex = 0;
    let headerIndex = 0;

    this.fieldDefs = [];
    this.headerConfig = [];
    this.columns = {};
    /* if properties map is existed then fetch the column configuration for all nested levels using util function */
    properties = data;
    /*call utility function to prepare fieldDefs for grid against given data (A MAX OF 10 COLUMNS ONLY)*/
    defaultFieldDefs = prepareFieldDefs(properties);

    /*append additional properties*/
    forEach(defaultFieldDefs, columnDef => {
      columnDef.binding = columnDef.field;
      columnDef.caption = columnDef.displayName;
      columnDef.pcDisplay = true;
      columnDef.mobileDisplay = true;
      columnDef.tabletDisplay = true;
      columnDef.searchable = true;
      columnDef.showinfilter = true;
      columnDef.type = 'string';
      // Fix for [WMS-19668] and [WMS-19669]- Adding index and headerIndex for Dynamic DB columns.
      // Note: we don't have column groups for Dynamic DB
      columnDef.index = columnIndex;
      columnDef.headerIndex = headerIndex;
      columnIndex++;
      headerIndex++;
    });

    defaultFieldDefs.forEach((col: any) => {
      this.columns[col.field] = col;
    });

    this.invokeEventCallback('beforedatarender', { $data: data, $columns: this.columns, data: data, columns: this.columns });

    defaultFieldDefs = [];
    // Apply the changes made by the user
    forEach(this.columns, val => {
      defaultFieldDefs.push(val);
    });

    this.generateDynamicColumns(defaultFieldDefs);
  }

  // Function to generate and compile the form fields from the metadata
  async generateDynamicColumns(columns: any) {
    this.fieldDefs = []; // empty the form fields
    // empty the filter field template refs.
    if (this.filterTmpl) (this.filterTmpl as any)._results = [];

    if (isEmpty(columns)) {
      return;
    }

    let tmpl = '';
    columns.forEach((col: any) => {
      let attrsTmpl = '';
      let customTmpl = '';
      forEach(col, (val, key) => {
        if (!isUndefined(val) && val !== '') {
          // If custom expression is present, keep it inside table column. Else, keep as attribute
          if (key === 'customExpression') {
            customTmpl = val;
          } else {
            attrsTmpl += ` ${key}="${val}"`;
          }
        }
      });
      tmpl += `<wm-table-column ${attrsTmpl} tableName="${this.name}${(this as any).$attrs.get('table_reference')}">${customTmpl}</wm-table-column>`;
    });
    this.dynamicTableRef.clear();
    if (!this._dynamicContext) {
      this._dynamicContext = Object.assign({}, this.viewParent);
      this._dynamicContext[(this.getAttr('wmTable') as string)] = this;
    }
    this.noOfColumns = columns.length;
    const componentFactoryRef = await this.dynamicComponentProvider.getComponentFactoryRef(
      'app-table-dynamic-' + this.widgetId,
      tmpl,
      {
        noCache: true,
        transpile: true
      });
    // const component = this.dynamicTableRef.createComponent(componentFactoryRef, 0, this.inj);
    extendProto(componentFactoryRef.instance, this.widget.context);
    this.$element.find('.dynamic-table-container')[0].appendChild(componentFactoryRef.location.nativeElement);
  }

  resetPageNavigation() {
    /*Check for sanity*/

    if (this.dataNavigator) {
      this.dataNavigator.resetPageNavigation();
    }
  }

  isNavigationEnabled() {
    return this.shownavigation && this.dataNavigator && this.dataNavigatorWatched;
  }

  isDataValid() {
    let error;
    const dataset = this._dataset || {};

    /*In case "data" contains "error" & "errorMessage", then display the error message in the grid.*/
    if (dataset.error) {
      error = dataset.error;
    }
    if (dataset.data && dataset.data.error) {
      if (dataset.data.errorMessage) {
        error = dataset.data.errorMessage;
      }
    }
    if (error) {
      this.setGridData([]);
      this.callDataGridMethod('setStatus', 'error', error);
      return false;
    }
    return true;
  }

  showFieldBasedOnScreenType(field: any) {
    let showField;
    if (isMobile() && this.viewport.isMobileType) {
      showField = field.mobileDisplay;
    } else if (this.viewport.isTabletType) {
      showField = field.tabletDisplay;
    } else {
      showField = field.pcDisplay;
    }
    return showField;
  }

  /* Check whether it is non-empty row. */
  isEmptyRecord(record: any) {
    const properties = Object.keys(record);
    let data,
      isDisplayed;

    return properties.every((prop, index) => {
      data = record[prop];
      /* If fieldDefs are missing, show all columns in data. */
      isDisplayed = (this.fieldDefs.length && isDefined(this.fieldDefs[index]) && this.showFieldBasedOnScreenType(this.fieldDefs[index])) || true;
      /*Validating only the displayed fields*/
      if (isDisplayed) {
        return (data === null || data === undefined || data === '');
      }
      return true;
    });
  }

  /* Function to remove the empty data. */
  removeEmptyRecords(serviceData: any) {
    const allRecords = serviceData;
    let filteredData = [];
    if (allRecords && allRecords.length) {
      /*Comparing and pushing the non-empty data columns*/
      filteredData = allRecords.filter((record: any) => {
        return record && !this.isEmptyRecord(record);
      });
    }
    return filteredData;
  }

  setGridData(serverData: any) {
    const data = this.filternullrecords ? this.removeEmptyRecords(serverData) : serverData;
    if (!this.variableInflight) {
      if (data && data.length === 0) {
        this.callDataGridMethod('setStatus', 'nodata', this.nodatamessage);
      } else {
        this.callDataGridMethod('setStatus', 'ready');
      }
    }
    this.gridData = data;
  }

  getSortExpr() {
    let sortExp;
    let pagingOptions;
    //TODO: check for datasource.execute
    // if (this.datasource && this.datasource?.execute(DataSource.Operation.IS_PAGEABLE)) {
    //   pagingOptions = this.datasource.execute(DataSource.Operation.GET_PAGING_OPTIONS);
    //   sortExp = isEmpty(pagingOptions) ? '' : getOrderByExpr(pagingOptions.sort);
    // }
    return sortExp || '';
  }

  registerRowActions(tableRowAction: any) {
    this.rowActions.push(tableRowAction);
  }

  registerFormField(name: any, formField: any) {
    this.formfields[name] = formField;
  }

  private clearActionRowVars() {
    this.actionRowIndex = undefined;
    this.actionRowPage = undefined;
    this.setDataGridOption('actionRowIndex', undefined);
    this.setDataGridOption('actionRowPage', undefined);
  }

  getClonedRowObject(rowData: any) {
    const row = cloneDeep(rowData);
    row.getProperty = (field: any) => {
      return get(row, field);
    };
    row.$isFirst = row.$index === 1;
    row.$isLast = this.gridData.length === row.$index;
    delete row.$$index;
    delete row.$$pk;
    return row;
  }

  addEventsToContext(context: any) {
    context.addNewRow = () => this.addNewRow();
    context.deleteRow = () => this.deleteRow();
    context.editRow = () => this.editRow();
  }

  private _redraw(forceRender: any) {
    if (forceRender) {
      this.datagridElement.datatable(this.gridOptions);
    } else {
      setTimeout(() => {
        this.callDataGridMethod('setColGroupWidths');
        this.callDataGridMethod('addOrRemoveScroll');
      });
    }
  }

  selectItem(item: any, data: any) {
    /* server is not updating immediately, so set the server data to success callback data */
    if (data) {
      this.serverData = data;
    }
    if (isObject(item)) {
      item = omitBy(item, (value) => {
        return isArray(value) && isEmpty(value);
      });
    }
    this.callDataGridMethod('selectRow', item, true);
  }

  toggleMessage(show: any, type: any, msg: any, header?: any) {
    if (show && msg) {
      this.appManager.notifyApp(msg, type, header);
    }
  }

  addRowIndex(row: any) {
    const rowData = getClonedObject(row);
    const rowIndex = indexOf(this.gridOptions.data, row);
    if (rowIndex < 0) {
      return row;
    }
    rowData.$index = rowIndex + 1;
    rowData.$isFirst = rowData.$index === 1;
    rowData.$isLast = this.gridData.length === rowData.$index;
    return rowData;
  }

  //Sets flags on data table like isBoundToLiveVariable etc.,
  private setFlags() {
    let binddataset = this.binddataset,
      variableType,
      boundToInnerDataSet,
      WidgetScopes: any = {},
      widgetType,
      widgetDetails;
    //Reset the variable and widget flags on $is before setting them.
    this.resetFlags();

    this.isBoundToVariable = startsWith(binddataset, 'Variables.') || startsWith(binddataset, 'bind:Variables.');
    this.isBoundToWidget = startsWith(binddataset, 'Widgets.') || startsWith(binddataset, 'bind:Widgets.');
    this.variableName = getVariableName(this);

    this.variable = this.variableService?.getVariableByName(this.variableName, this);
    if (this.isBoundToVariable && this.variable) {
      this.variableType = variableType = this.variable.category;
      this.isBoundToStaticVariable = variableType === 'wm.Variable';
      this.isBoundToWebSocketVariable = variableType === 'wm.WebSocketVariable';
      this.isBoundToLiveVariable = variableType === 'wm.LiveVariable';
      boundToInnerDataSet = includes(binddataset, 'dataSet.');
      if (this.isBoundToLiveVariable) {
        this.isBoundToLiveVariableRoot = !boundToInnerDataSet;
      } else {
        this.isBoundToServiceVariable = (variableType === 'wm.ServiceVariable' || variableType === 'wm.CrudVariable');
        this.isBoundToDataServiceType = (this.isBoundToServiceVariable && this.variable.serviceType === 'DataService');
        if (this.isBoundToDataServiceType) {
          this.isBoundToProcedureServiceVariable = this.variable.controller === 'ProcedureExecution';
          this.isBoundToQueryServiceVariable = this.variable.controller === 'QueryExecution';
        }
      }
      if (this.isBoundToServiceVariable) {
        // Checking if grid is bound to service variable, for which data cannot be loaded in studio mode
        this.gridData = [];
        this.callDataGridMethod('setStatus', 'error', this.appLocale['MESSAGE_GRID_CANNOT_LOAD_DATA_IN_STUDIO']);
      }
    } else if (this.isBoundToWidget) {
      this.isBoundToSelectedItem = binddataset.indexOf('selecteditem') !== -1;
      this.isBoundToSelectedItemSubset = binddataset.indexOf('selecteditem.') !== -1;
      let getWidgetsByType = runtimeEventManager.getService('getWidgetsByType');
      // fetch the list of widgets
      WidgetScopes = getWidgetsByType('form-widgets');
      //Get the reference widget name
      widgetDetails = getBoundWidgetDetails(binddataset, WidgetScopes);
      this.widgetName = widgetDetails.widgetName;
      let widget = get(WidgetScopes, this.widgetName);
      widgetType = widget?.widgetType;
      this.isBoundToFilter = widgetType === 'wm-livefilter';
    }
  }

  private resetFlags() {
    this.isBoundToStaticVariable = false;
    this.isBoundToLiveVariable = false;
    this.isBoundToLiveVariableRoot = false;
    this.isBoundToServiceVariable = false;
    this.isBoundToDataServiceType = false;
    this.isBoundToProcedureServiceVariable = false;
    this.isBoundToQueryServiceVariable = false;
    this.isBoundToSelectedItem = false;
    this.isBoundToSelectedItemSubset = false;
    this.isBoundToFilter = false;
  }

  private showExportOptions() {
    //showing export options in case of widget bound to live variables and query service variable.
    return this.isBoundToLiveVariable || this.isBoundToFilter || (this.isBoundToQueryServiceVariable && this.variable.isList);
  }

  resetFormControl(ctrl: any) {
    ctrl.markAsUntouched();
    ctrl.markAsPristine();
  }

  clearForm(newRow?: any) {
    const ctrls = this.ngform.controls;
    keys(this.ngform.controls).forEach(key => {
      // If new row, clear the controls in the new row. Else, clear the controls in edit row
      if (!key.endsWith('_filter') && ((key.endsWith('_new') && newRow) || (!key.endsWith('_new') && !newRow))) {
        ctrls[key].setValue('');
        this.resetFormControl(ctrls[key]);
      }
    });
  }

  applyRowContextToCustomWidget(expr: HTMLElement, context: any) {
    let children = expr.children;
    forEach(children, (el: any) => {
      extend(get(el, 'widget').context, context);
    });
  }
  // toggle active class for first row of the grid
  toggleFirstRowSelectClass() {
    let firstRow = this.datagridElement.find('tr.app-datagrid-row');
    if (firstRow && firstRow.length) {
      toBoolean(this.nativeElement.getAttribute('gridfirstrowselect')) ? $(firstRow[0]).addClass('active') : $(firstRow[0]).removeClass('active');
    }
  }

  override ngOnDestroy() {
    if (this.widgetId) {
      runtimeEventManager.off(this.widgetId + 'table-compiled');
      runtimeEventManager.off('dataset-modified' + '_' + this.widgetId);
    }
    super.ngOnDestroy()
  }
}

